import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Checkbox, Input, Button, Radio, Select, Flex } from "antd"
import styles from "./CustomQuestions.module.css"
import { getBookingQuestionnaire, submitQuestionnaire } from "store/question"
import { getWaiverSubmissionsByTripBooking } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { useTranslation } from "react-i18next"
import { toaster } from "components/common/Toast"

const CustomQuestions = ({ uuid, bookingQuestionnaireId, visible = true, onClose, onSubmit }) => {
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const { t } = useTranslation()
  const { questionaire_data } = useSelector((state) => state.question)
  const questions = questionaire_data?.data?.questions || []
  const booking_questionnaire_answers = questionaire_data?.data?.booking_questionnaire_answers || []
  console.log(questions, "booking_questionnaire_answers")
  const loading = questionaire_data?.loading
  const logoSrc = theme === themes.sportswoman ? bgWoman : bgMan
  const { loading: isLoading } = useSelector((state) => state.question)
  useEffect(() => {
    if (bookingQuestionnaireId) {
      dispatch(getBookingQuestionnaire(bookingQuestionnaireId))
    }
  }, [bookingQuestionnaireId, dispatch])

  const [form] = Form.useForm()

  // Function to get initial form values from existing answers
  const getInitialValues = () => {
    const initialValues = {}
    
    booking_questionnaire_answers.forEach((answer) => {
      const fieldName = `question_${answer.question_id}`
      
      if (answer.questions_option_id) {
        // For dropdown/radio/checkbox - find the option text by ID
        const question = questions.find((q) => q.id === answer.question_id)
        if (question) {
          const option = question.questions_options.find((opt) => opt.id === answer.questions_option_id)
          if (option) {
            initialValues[fieldName] = option.option_text
          }
        }
      } else if (answer.answer_text) {
        // For paragraph questions
        initialValues[fieldName] = answer.answer_text
      }
    })
    
    return initialValues
  }

  // Set form initial values when answers are available
  useEffect(() => {
    if (booking_questionnaire_answers.length > 0 && questions.length > 0) {
      const initialValues = getInitialValues()
      form.setFieldsValue(initialValues)
    }
  }, [booking_questionnaire_answers, questions, form])

  const formatAnswer = (question, value) => {
    if (!value) {
      return {
        question_id: question.id,
        answer_text: "",
        questions_option_id: null
      }
    }

    if (question.question_type === "paragraph") {
      return {
        question_id: question.id,
        answer_text: value,
        questions_option_id: null
      }
    }

    // For radio, dropdown - find the selected option ID (single value)
    if (question.question_type === "radio" || question.question_type === "dropdown") {
      const option = question.questions_options.find((opt) => opt.option_text === value)
      return {
        question_id: question.id,
        answer_text: "",
        questions_option_id: option ? option.id : null
      }
    }

    // For checkbox - find the selected option IDs (multiple values)
    const selectedOptions = Array.isArray(value) ? value : [value]
    const optionIds = selectedOptions
      .map((optionText) => {
        const option = question.questions_options.find((opt) => opt.option_text === optionText)
        return option ? option.id : null
      })
      .filter(Boolean)

    return {
      question_id: question.id,
      answer_text: "",
      questions_option_id: optionIds
    }
  }

  const handleFinish = async (values) => {
    try {
      // Validate all required fields
      const requiredFields = questions.filter((q) => q.required)
      const missingFields = []

      requiredFields.forEach((q) => {
        const fieldName = `question_${q.id}`
        if (!values[fieldName] || (Array.isArray(values[fieldName]) && values[fieldName].length === 0)) {
          missingFields.push(fieldName)
        }
      })

      if (missingFields.length > 0) {
        const firstError = document.querySelector(`[name="${missingFields[0]}"]`)
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" })
        }
        return
      }

      // Format the answers according to the required payload
      const answers = questions.map((question) => {
        const fieldName = `question_${question.id}`
        return formatAnswer(question, values[fieldName])
      })
      if (onSubmit) {
        await onSubmit({ answers })
      }
      // If no onSubmit prop is provided, use the default submission
      const res = await dispatch(submitQuestionnaire(bookingQuestionnaireId, answers))
      if (res.message) {
        toaster.success(res.message)
        dispatch(getWaiverSubmissionsByTripBooking(uuid))
      }
      if (onClose) onClose()
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }

  const renderQuestionInput = (question) => {
    const commonProps = {
      key: question.id,
      label: question.question_text + (question.required ? "*" : ""),
      name: `question_${question.id}`,
      rules: question.required
        ? [
            {
              validator: (_, value) => {
                if (question.required && (!value || (Array.isArray(value) && value.length === 0))) {
                  return Promise.reject(new Error(t("settings.question_required")))
                }
                return Promise.resolve()
              }
            }
          ]
        : [],
      className: styles.formItem
    }

    switch (question.question_type) {
      case "checkbox":
        return (
          <Form.Item {...commonProps}>
            <Checkbox.Group className={styles.checkboxGroup}>
              {question.questions_options.map((opt) => (
                <Checkbox key={opt.id} value={opt.option_text} className={styles.checkbox}>
                  {opt.option_text}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        )

      case "radio":
        return (
          <Form.Item {...commonProps}>
            <Radio.Group className={styles.radioGroup}>
              {question.questions_options.map((opt) => (
                <Radio key={opt.id} value={opt.option_text} className={styles.radio}>
                  {opt.option_text}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        )

      case "dropdown":
        return (
          <Form.Item {...commonProps}>
            <Select className={styles.select} placeholder={t("settings.select_placeholder")}>
              {question.questions_options.map((opt) => (
                <Select.Option key={opt.id} value={opt.option_text}>
                  {opt.option_text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )

      case "paragraph":
        return (
          <Form.Item {...commonProps}>
            <Input.TextArea rows={4} className={styles.textarea} placeholder={t("settings.paragraph_placeholder")} />
          </Form.Item>
        )

      default:
        return null
    }
  }

  return (
    <Modal open={visible} onCancel={onClose} footer={null} centered className={styles.modal} width={800} destroyOnClose>
      <div className={styles.header}>{t("settings.quesionnaire")}</div>
      <FirstLoading new={!loading}>
        <Form form={form} layout="vertical" onFinish={handleFinish} className={styles.form}>
          <div className={styles.questionCard}>{questions.map(renderQuestionInput)}</div>
          {/* <Flex justify="space-between" align="center" gap={6} className={styles.customQuestionsFooter}> */}
          {/* <div className={styles.poweredBy}>
              {t("settings.powered_by")}{" "}
              <span className={styles.logo}>
                <img className={styles.logoImage} src={logoSrc} alt="" />
              </span>
            </div> */}
          <Flex gap={6} className={styles.buttonGroup}>
            <Button onClick={onClose} color="primary" variant="outlined">
              {t("global.cancel")}
            </Button>
            <Button htmlType="submit" color="primary" variant="solid" loading={isLoading} disabled={isLoading}>
              Send form
            </Button>
          </Flex>
          {/* </Flex> */}
        </Form>
      </FirstLoading>
    </Modal>
  )
}

CustomQuestions.propTypes = {
  bookingQuestionnaireId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func
}

CustomQuestions.defaultProps = {
  visible: true,
  bookingQuestionnaireId: "",
  onSubmit: () => {}
}

export default CustomQuestions
